import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeftIcon,
  PhoneIcon,
  ClockIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Card, Badge, Button, Spinner } from '@realvisor/ui';
import {
  useCampaign,
  useCampaignQueue,
  useLogCall,
  useTakeBatch,
  type CampaignQueueContact,
  type CampaignOutcome,
} from '@/hooks/useCampaigns';

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

function formatStopwatch(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function TemperatureBadge({ temperature }: { temperature: string | null }) {
  if (temperature === 'hot') {
    return <Badge variant="danger">Horky</Badge>;
  }
  if (temperature === 'warm') {
    return <Badge variant="warning">Teply</Badge>;
  }
  return <Badge variant="info">Studeny</Badge>;
}

function maskPhone(phone: string): string {
  if (phone.length <= 4) return phone;
  return phone.slice(0, 4) + ' XXX XXX XXX';
}

function ActivityIcon({ type }: { type: string }) {
  const cls = 'h-4 w-4 shrink-0';
  switch (type) {
    case 'call':
      return <PhoneIcon className={`${cls} text-green-500`} />;
    case 'email':
      return <EnvelopeIcon className={`${cls} text-blue-500`} />;
    default:
      return <BriefcaseIcon className={`${cls} text-gray-400`} />;
  }
}

export default function CampaignCallView() {
  const { id } = useParams<{ id: string }>();
  const campaignId = id || '';

  const { data: campaign, isLoading: campaignLoading } = useCampaign(campaignId);
  const { data: queue, isLoading: queueLoading } = useCampaignQueue(campaignId);
  const logCall = useLogCall();
  const takeBatch = useTakeBatch();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [stopwatchRunning, setStopwatchRunning] = useState(false);
  const [stopwatchSeconds, setStopwatchSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [selectedOutcomeId, setSelectedOutcomeId] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const [callNotes, setCallNotes] = useState('');

  const activeQueue = (queue || []).filter((q) => q.status !== 'completed');
  const currentContact: CampaignQueueContact | undefined = activeQueue[currentIndex];
  const outcomes: CampaignOutcome[] = campaign?.outcomes || [];
  const selectedOutcome = outcomes.find((o) => o.id === selectedOutcomeId);
  const isCallback = selectedOutcome?.code === 'callback' && !(selectedOutcome as any).followUpDays;
  const totalInQueue = queue?.length ?? 0;
  const completedInQueue = (queue || []).filter((q) => q.status === 'completed').length;

  // Contact data shortcuts
  const c = currentContact?.contact as any;
  const company = c?.company;
  const leads = c?.leads || [];
  const activities = c?.activities || [];

  useEffect(() => {
    if (stopwatchRunning) {
      intervalRef.current = setInterval(() => setStopwatchSeconds((p) => p + 1), 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [stopwatchRunning]);

  useEffect(() => {
    setPhoneRevealed(false);
    setStopwatchRunning(false);
    setStopwatchSeconds(0);
    setSelectedOutcomeId('');
    setCallbackDate('');
    setCallbackTime('');
    setCallNotes('');
  }, [currentIndex, queue]);

  const handleSaveAndNext = useCallback(async () => {
    if (!currentContact || !selectedOutcomeId) {
      toast.error('Vyberte vysledek hovoru');
      return;
    }
    let callbackAt: string | undefined;
    if (isCallback && callbackDate && callbackTime) {
      callbackAt = new Date(`${callbackDate}T${callbackTime}`).toISOString();
    }
    try {
      await logCall.mutateAsync({
        campaignId,
        contactId: currentContact.id,
        data: { outcomeId: selectedOutcomeId, duration: stopwatchSeconds, notes: callNotes || undefined, callbackAt },
      });
      toast.success('Hovor ulozen');
      if (currentIndex < activeQueue.length - 1) {
        setCurrentIndex((p) => p + 1);
      } else {
        setCurrentIndex(0);
      }
    } catch {
      toast.error('Chyba pri ukladani hovoru');
    }
  }, [currentContact, selectedOutcomeId, isCallback, callbackDate, callbackTime, stopwatchSeconds, callNotes, campaignId, currentIndex, activeQueue.length, logCall]);

  const handleTakeBatch = async () => {
    try {
      await takeBatch.mutateAsync(campaignId);
      setCurrentIndex(0);
      toast.success('Balik prirazen');
    } catch {
      toast.error('Nepodarilo se vzit balik');
    }
  };

  if (campaignLoading || queueLoading) {
    return <div className="flex items-center justify-center py-12"><Spinner size="lg" /></div>;
  }

  if (!campaign) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Kampan nebyla nalezena</p>
          <Link to="/callcenter/campaigns" className="mt-4 inline-block text-primary-600 hover:underline">Zpet na seznam</Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/callcenter/campaigns/${campaignId}`} className="flex items-center justify-center rounded-lg border border-gray-200 p-2 hover:bg-gray-50">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{campaign.name}</h1>
            <p className="text-sm text-gray-500">
              {completedInQueue} / {totalInQueue} provolano
              {activeQueue.length > 0 && ` | Kontakt ${currentIndex + 1} z ${activeQueue.length}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Stopwatch inline in header */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5">
            <ClockIcon className="h-4 w-4 text-gray-400" />
            <span className="text-lg font-mono font-bold text-gray-900 tabular-nums">{formatStopwatch(stopwatchSeconds)}</span>
            {!stopwatchRunning ? (
              <button onClick={() => setStopwatchRunning(true)} className="rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700">Start</button>
            ) : (
              <button onClick={() => setStopwatchRunning(false)} className="rounded bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700">Stop</button>
            )}
            <button onClick={() => { setStopwatchRunning(false); setStopwatchSeconds(0); }} className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50">Reset</button>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {!currentContact ? (
        <Card padding="lg">
          <div className="text-center py-8 space-y-4">
            <p className="text-lg text-gray-500">Fronta je prazdna</p>
            <Button onClick={handleTakeBatch} disabled={takeBatch.isPending}>
              {takeBatch.isPending ? 'Nacitam...' : 'Vzit dalsi balik'}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT COLUMN: Contact info + Context */}
          <div className="lg:col-span-2 space-y-4">
            {/* Contact card */}
            <Card>
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {c.firstName} {c.lastName || ''}
                      </h2>
                      <TemperatureBadge temperature={c.temperature} />
                    </div>
                    {c.position && (
                      <p className="text-sm text-gray-500 mt-0.5">{c.position}{c.department ? ` / ${c.department}` : ''}</p>
                    )}
                    {c.tags?.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {c.tags.map((tag: string) => <Badge key={tag} variant="default">{tag}</Badge>)}
                      </div>
                    )}
                  </div>
                  <Link to={`/contacts/${c.id}`} target="_blank" className="text-xs text-blue-600 hover:underline shrink-0">
                    Otevrit detail
                  </Link>
                </div>

                <div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  {/* Phone */}
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Telefon</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      {c.phone ? (
                        phoneRevealed ? (
                          <a href={`tel:${c.phone}`} className="font-medium text-blue-600 hover:underline flex items-center gap-1">
                            <PhoneIcon className="h-4 w-4 text-green-600" />
                            {c.phone}
                          </a>
                        ) : (
                          <>
                            <span className="font-medium text-gray-400">{maskPhone(c.phone)}</span>
                            <Button variant="outline" size="sm" onClick={() => setPhoneRevealed(true)}>Zobrazit</Button>
                          </>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Email</span>
                    <p className="mt-0.5 font-medium text-gray-900">{c.email || c.personalEmail || '-'}</p>
                  </div>

                  {/* Personal phone */}
                  {c.personalPhone && (
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Osobni telefon</span>
                      <p className="mt-0.5 font-medium text-gray-900">{c.personalPhone}</p>
                    </div>
                  )}

                  {/* LinkedIn */}
                  {c.linkedinUrl && (
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wide">LinkedIn</span>
                      <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" className="mt-0.5 flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <LinkIcon className="h-3.5 w-3.5" /> Profil
                      </a>
                    </div>
                  )}
                </div>

                {/* Company */}
                {company && (
                  <div className="mt-4 rounded-lg bg-gray-50 p-3">
                    <div className="flex items-center gap-2">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{company.name}</span>
                      {company.ico && <span className="text-xs text-gray-500">ICO: {company.ico}</span>}
                    </div>
                    <div className="flex gap-4 mt-1 text-sm text-gray-500">
                      {company.phone && <span>{company.phone}</span>}
                      {company.website && (
                        <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <GlobeAltIcon className="h-3.5 w-3.5" /> Web
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {c.notes && (
                  <div className="mt-3">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Poznamky ke kontaktu</span>
                    <p className="mt-0.5 text-sm text-gray-700 whitespace-pre-wrap bg-yellow-50 rounded p-2 border border-yellow-100">{c.notes}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Leads */}
            {leads.length > 0 && (
              <Card>
                <div className="p-5">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Leady ({leads.length})</h3>
                  <div className="space-y-2">
                    {leads.map((lead: any) => (
                      <Link
                        key={lead.id}
                        to={`/leads/${lead.id}`}
                        target="_blank"
                        className="flex items-center justify-between rounded-lg border border-gray-100 p-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {lead.currentStage && (
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: lead.currentStage.color || '#6B7280' }}
                            />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {lead.currentStage?.name || 'Bez faze'}
                              <span className="text-gray-400 font-normal ml-2">{lead.project?.name}</span>
                            </p>
                            {lead.notes && (
                              <p className="text-xs text-gray-500 truncate max-w-xs">{lead.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-gray-500">{formatDateTime(lead.createdAt)}</p>
                          <Badge variant={lead.status === 'open' ? 'success' : lead.status === 'won' ? 'info' : 'default'}>
                            {lead.status}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Activities / History */}
            {(activities.length > 0 || (currentContact?.callLogs?.length ?? 0) > 0) && (
              <Card>
                <div className="p-5">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Historie aktivit</h3>
                  <div className="space-y-1.5">
                    {/* Campaign call logs first */}
                    {currentContact?.callLogs?.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 rounded-lg border border-gray-100 p-2.5 text-sm">
                        <PhoneIcon className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{log.outcome.name}</span>
                            <span className="text-gray-400">{log.duration}s</span>
                            <span className="text-xs text-gray-400">{formatDateTime(log.createdAt)}</span>
                          </div>
                          {log.notes && <p className="text-gray-500 mt-0.5">{log.notes}</p>}
                        </div>
                      </div>
                    ))}
                    {/* General activities */}
                    {activities.map((act: any) => (
                      <div key={act.id} className="flex items-start gap-3 rounded-lg border border-gray-100 p-2.5 text-sm">
                        <ActivityIcon type={act.type} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{act.subject || act.type}</span>
                            <span className="text-xs text-gray-400">{formatDateTime(act.createdAt)}</span>
                          </div>
                          {act.body && <p className="text-gray-500 mt-0.5 truncate">{act.body}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* RIGHT COLUMN: Script + Call form */}
          <div className="space-y-4">
            {/* Call script */}
            {campaign.scriptContent && (
              <Card>
                <div className="p-5">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Call skript</h3>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-amber-50 rounded-lg p-4 border border-amber-200 max-h-80 overflow-y-auto">
                    {campaign.scriptContent}
                  </div>
                </div>
              </Card>
            )}

            {/* Outcome form */}
            <Card>
              <div className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Vysledek hovoru</h3>

                <div className="space-y-1.5 mb-4">
                  {outcomes.map((outcome) => (
                    <label
                      key={outcome.id}
                      className={`flex items-center gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors ${
                        selectedOutcomeId === outcome.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="outcome"
                        value={outcome.id}
                        checked={selectedOutcomeId === outcome.id}
                        onChange={(e) => setSelectedOutcomeId(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-900 flex-1">
                        {outcome.name}
                        {(outcome as any).followUpDays && (
                          <span className="text-xs text-gray-400 ml-1">
                            (follow-up {(outcome as any).followUpDays}d)
                          </span>
                        )}
                      </span>
                      {outcome.rewardAmount != null && Number(outcome.rewardAmount) > 0 && (
                        <span className="text-xs font-medium text-green-600">{outcome.rewardAmount} Kc</span>
                      )}
                    </label>
                  ))}
                </div>

                {/* Callback date/time */}
                {isCallback && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Datum</label>
                      <input
                        type="date"
                        value={callbackDate}
                        onChange={(e) => setCallbackDate(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cas</label>
                      <input
                        type="time"
                        value={callbackTime}
                        onChange={(e) => setCallbackTime(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Poznamka</label>
                  <textarea
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Poznamka k hovoru..."
                  />
                </div>

                {/* Submit */}
                <Button className="w-full" onClick={handleSaveAndNext} disabled={!selectedOutcomeId || logCall.isPending}>
                  {logCall.isPending ? 'Ukladam...' : 'Ulozit a dalsi'}
                </Button>
              </div>
            </Card>

            {/* Queue navigation */}
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentIndex <= 0}
                    onClick={() => setCurrentIndex((p) => p - 1)}
                  >
                    Predchozi
                  </Button>
                  <span className="text-sm text-gray-500">{currentIndex + 1} / {activeQueue.length}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentIndex >= activeQueue.length - 1}
                    onClick={() => setCurrentIndex((p) => p + 1)}
                  >
                    Dalsi
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
